import { useMutation } from "@apollo/client";
import { useDishTypes } from "hooks/useDishTypes";
import { useFridgeInformation } from "hooks/useFridgeInformation";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { useCallback, useMemo, useRef } from "react";
import { selectFridgeLightingMode } from "slices/fridgeLightingSlice";
import type { FridgeDishesUpdate, LightingModeType } from "types";
import type { DishTypeLightUpdate } from "types/mainTypes";
import { getTabletType, TabletTypeEnum } from "utils/browser/localStorage";
import { SWITCH_FRIDGE_MODE_MUTATION, TOGGLE_SPOTLIGHT_MODE_MUTATION } from "utils/gqlOperations";
import { createDefaultLight, createSpotlightLight, dishLightingStateCreator } from "utils/lightingUtils";

export const useFridgeLights = () => {
	const { id } = useFridgeInformation();
	const { data: dishTypes } = useDishTypes();
	const fridgeLightingMode = useAppSelector(selectFridgeLightingMode);
	const [updateLights] = useMutation<FridgeDishesUpdate>(TOGGLE_SPOTLIGHT_MODE_MUTATION);
	const [toggleFridgeLightMode] = useMutation(SWITCH_FRIDGE_MODE_MUTATION);

	const toggleFridgeLightingMode = useCallback(
		(mode: LightingModeType) => {
			const tabletType = getTabletType();
			if (id == null || tabletType !== TabletTypeEnum.Master) {
				return;
			}

			toggleFridgeLightMode({
				variables: {
					input: {
						id: id,
						lighting: {
							mode: mode,
						},
					},
				},
			});
		},
		[id, toggleFridgeLightMode],
	);

	// Latest-ref of fridgeLightingMode + a stable setter that skips redundant dispatches.
	// Two callers (MealList effects + useMealListEvents) used to maintain their own local
	// `lightsRef` to break the subscription-echo flicker loop; the duplication is the bug
	// magnet. The synchronous ref update inside the callback preserves the cross-effect
	// invariant that within a single commit the second call to set(Active) after the first
	// observes the post-mutation value and short-circuits.
	const lightsRef = useRef(fridgeLightingMode);
	lightsRef.current = fridgeLightingMode;

	const setLightingModeIfChanged = useCallback(
		(target: LightingModeType) => {
			if (lightsRef.current === target) {
				return;
			}
			lightsRef.current = target;
			toggleFridgeLightingMode(target);
		},
		[toggleFridgeLightingMode],
	);

	const toggleLight = useCallback(
		(input: DishTypeLightUpdate[]) => {
			const tabletType = getTabletType();
			if (id == null || tabletType !== TabletTypeEnum.Master) {
				return;
			}

			updateLights({
				variables: {
					input: {
						id: id,
						dishes: {
							types: input,
						},
					},
				},
			});
		},
		[id, updateLights],
	);

	const highlightSingleDishType = useCallback(
		(
			toggleLight: (input: DishTypeLightUpdate[]) => void,
			availableDishTypeIds: Set<string>,
			highlightedDishTypeId: string | null,
		) => {
			const updatedLights = Array.from(availableDishTypeIds).map((dishTypeId) =>
				dishTypeId === highlightedDishTypeId ? createSpotlightLight(dishTypeId) : createDefaultLight(dishTypeId),
			);
			toggleLight(updatedLights);
		},
		[],
	);

	const highlightFilteredDishes = useCallback(
		(
			filteredDishTypesIds: string[],
			availableDishTypeIds: Set<string>,
			toggleLight: (input: DishTypeLightUpdate[]) => void,
			highlightedDishTypeId: string | null,
		) => {
			if (highlightedDishTypeId && filteredDishTypesIds.includes(highlightedDishTypeId)) {
				highlightSingleDishType(toggleLight, availableDishTypeIds, highlightedDishTypeId);
			} else {
				toggleLight(dishLightingStateCreator(filteredDishTypesIds, availableDishTypeIds));
			}
		},
		[highlightSingleDishType],
	);

	const dishTypeLights = useMemo(
		() =>
			dishTypes?.map((type) => ({
				id: type.id,
				lightingMode: type.lighting?.mode,
			})),
		[dishTypes],
	);

	return {
		toggleLight: toggleLight,
		toggleFridgeLightingMode: toggleFridgeLightingMode,
		setLightingModeIfChanged: setLightingModeIfChanged,
		dishTypeLights: dishTypeLights,
		lights: fridgeLightingMode,
		highlightSingleDishType: highlightSingleDishType,
		highlightFilteredDishes: highlightFilteredDishes,
	};
};
